import { useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Modal, Textarea } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useSupportSession } from '@/hooks/useSupportSession'
import {
  BLOCKER_CODES,
  BLOCKER_NOTE_LIMIT,
  blockerTriggerFor,
  markBlockerAsked,
  subscribeBlocker,
  useAnswerBlocker,
  type BlockerCode,
  type BlockerTrigger,
} from './blockerQuestion'

/**
 * EP-160 S8: "ติดตรงไหน?". Mounted once on the dashboard; shows itself for an
 * owner or admin when a trigger is due (see blockerQuestion.ts) and asks once
 * per trigger. Closing it counts as ข้าม. Never shown in a FlowRent support
 * session: the question is the owner's to answer, not the support team's.
 */
export function BlockerQuestionModal() {
  const { user } = useAuth()
  const supportSession = useSupportSession().active
  const trigger = useSyncExternalStore(subscribeBlocker, () => blockerTriggerFor(user))
  if (!user || !trigger || supportSession) return null
  // Keyed so a second trigger in the same visit starts with a clean form.
  return <BlockerQuestionDialog key={trigger} accountId={user.account.id} trigger={trigger} />
}

interface BlockerQuestionDialogProps {
  accountId: number
  trigger: BlockerTrigger
}

function BlockerQuestionDialog({ accountId, trigger }: BlockerQuestionDialogProps) {
  const { t } = useTranslation('onboarding')
  const answer = useAnswerBlocker()
  const [code, setCode] = useState<BlockerCode | null>(null)
  const [note, setNote] = useState('')

  // A skip is asked and done: the dialog closes at once and the stamp is the
  // server's business, so a failed skip is not worth interrupting anyone for.
  const skip = () => {
    if (answer.isPending) return
    answer.mutate({ code: 'skipped', trigger })
    markBlockerAsked(accountId, trigger)
  }

  const send = () => {
    if (!code) return
    const trimmed = note.trim()
    answer.mutate(
      { code, trigger, ...(trimmed ? { note: trimmed } : {}) },
      { onSuccess: () => markBlockerAsked(accountId, trigger) },
    )
  }

  return (
    <Modal
      isOpen
      onClose={skip}
      title={t('blocker.title')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={skip} disabled={answer.isPending} data-testid="blocker-skip">
            {t('blocker.skip')}
          </Button>
          <Button
            variant="primary"
            onClick={send}
            disabled={!code}
            loading={answer.isPending}
            data-testid="blocker-send"
          >
            {t('blocker.send')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="blocker-question">
        <p className="text-sm text-base-content/70">{t('blocker.intro')}</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('blocker.title')}>
          {BLOCKER_CODES.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={code === option ? 'primary' : 'outline'}
              aria-pressed={code === option}
              onClick={() => setCode(option)}
              data-testid={`blocker-code-${option}`}
            >
              {t(`blocker.codes.${option}`)}
            </Button>
          ))}
        </div>
        <Textarea
          id="blocker-note"
          label={t('blocker.note_label')}
          value={note}
          maxLength={BLOCKER_NOTE_LIMIT}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
        />
        {answer.isError && <Alert variant="error">{t('blocker.error')}</Alert>}
      </div>
    </Modal>
  )
}
